<script setup lang="ts">
/**
 * THE PEOPLE WHO WORK HERE — added, changed, given a new PIN, seen off.
 *
 * Every change a till trusts (a PIN, a role, leaving) reaches each till at its
 * next roster, which forgets what it held for that person; the screen says so,
 * because "the old PIN still works for a minute" should not be a surprise.
 * Nobody hands out a role above their own, and the list only offers those.
 */
import { computed, onMounted, ref } from "vue";
import type { ConsoleApi, StaffRow } from "../api";
import StaffSheet from "./StaffSheet.vue";

type StaffApi = Pick<ConsoleApi, "staff" | "addStaff" | "updateStaff" | "resetPin">;

const props = defineProps<{
  api: StaffApi;
  /** Who is signed in, to offer only the roles they may hand out. */
  me: { roleId: string } | null;
  t: (key: string) => string;
}>();

const RANKED = ["viewer", "staff", "shift_lead", "manager", "admin", "owner"] as const;

const people = ref<readonly StaffRow[]>([]);
const status = ref<"loading" | "ready" | "failed">("loading");
const editing = ref<StaffRow | "new" | null>(null);
const busy = ref(false);
const error = ref<string | null>(null);
const notice = ref<string | null>(null);

const load = async (): Promise<void> => {
  try {
    people.value = (await props.api.staff()).staff;
    status.value = "ready";
  } catch {
    status.value = "failed";
  }
};
onMounted(load);

/**
 * `staff.manage` is owner and admin (`@gf/kernel/authz`). Anybody else sees
 * the list and nothing to press — a form that can only fail is worse than none.
 */
const canManage = computed(() => props.me === null || ["owner", "admin"].includes(props.me.roleId));

const roles = computed(() => {
  const mine = RANKED.indexOf((props.me?.roleId ?? "owner") as (typeof RANKED)[number]);
  return RANKED.filter((_, i) => i <= (mine < 0 ? 0 : mine)).slice().reverse();
});

const roleName = (roleId: string): string => {
  const key = `role.${roleId}`;
  const name = props.t(key);
  return name === key ? roleId : name;
};

const active = computed(() => people.value.filter((p) => p.active !== false));
const left = computed(() => people.value.filter((p) => p.active === false));

const save = async (change: { displayName: string; roleId: string; active: boolean; pin: string | null }): Promise<void> => {
  const target = editing.value;
  busy.value = true;
  error.value = null;
  try {
    if (target === "new") {
      await props.api.addStaff({ displayName: change.displayName, roleId: change.roleId, pin: change.pin! });
      notice.value = props.t("staff.added");
    } else if (target) {
      const edits: { displayName?: string; roleId?: string; active?: boolean } = {};
      if (change.displayName !== target.displayName) edits.displayName = change.displayName;
      if (change.roleId !== target.roleId) edits.roleId = change.roleId;
      if (change.active !== (target.active !== false)) edits.active = change.active;
      if (Object.keys(edits).length > 0) await props.api.updateStaff(target.staffId, edits);
      if (change.pin) await props.api.resetPin(target.staffId, change.pin);
      const trusted = change.pin !== null || edits.roleId !== undefined || edits.active !== undefined;
      notice.value = props.t(trusted ? "staff.savedTills" : "staff.saved");
    }
    editing.value = null;
    await load();
  } catch (e) {
    // The server's own sentence — "above your own role", "six or more digits".
    error.value = e instanceof Error && e.message ? e.message : props.t("common.failed");
  } finally {
    busy.value = false;
  }
};
</script>

<template>
  <section class="staff">
    <p v-if="status === 'loading'" class="gf-muted" role="status">{{ t("common.loading") }}</p>
    <div v-else-if="status === 'failed'" class="gf-note gf-note--bad" role="alert">
      {{ t("common.failed") }}
      <button type="button" class="gf-button" @click="load">{{ t("common.retry") }}</button>
    </div>

    <template v-else>
      <header class="staff__bar">
        <p class="staff__count gf-muted">{{ t("staff.count").replace("{n}", String(active.length)) }}</p>
        <button v-if="canManage" type="button" class="gf-button gf-button--primary" data-testid="add-staff" @click="editing = 'new'">
          {{ t("staff.add") }}
        </button>
      </header>

      <p v-if="notice" class="staff__notice" role="status">{{ notice }}</p>

      <!-- In a panel, like every other table in the office: it sat bare on the page. -->
      <div class="gf-panel gf-panel--flush">
      <table class="gf-table staff__table">
        <thead>
          <tr>
            <th scope="col">{{ t("staff.name") }}</th>
            <th scope="col">{{ t("staff.role") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="p in active"
            :key="p.staffId"
            class="staff__row"
            :class="{ 'staff__row--editable': canManage }"
            :data-staff="p.staffId"
            @click="canManage && (editing = p)"
          >
            <th scope="row">{{ p.displayName }}</th>
            <td class="gf-muted">{{ roleName(p.roleId) }}</td>
          </tr>
        </tbody>
      </table>
      </div>

      <details v-if="left.length > 0" class="staff__left">
        <summary class="gf-muted">{{ t("staff.left") }} ({{ left.length }})</summary>
        <ul class="staff__leftList">
          <li v-for="p in left" :key="p.staffId">
            <button type="button" class="staff__leftPerson" :data-staff="p.staffId" :disabled="!canManage" @click="editing = p">
              {{ p.displayName }} <span class="gf-muted">· {{ roleName(p.roleId) }}</span>
            </button>
          </li>
        </ul>
      </details>
    </template>

    <StaffSheet
      v-if="editing"
      :key="editing === 'new' ? 'new' : editing.staffId"
      :person="editing === 'new' ? null : editing"
      :roles="roles"
      :t="t"
      :busy="busy"
      :error="error"
      @save="save"
      @cancel="
        editing = null;
        error = null;
      "
    />
  </section>
</template>

<style scoped>
.staff {
  display: flex;
  flex-direction: column;
  gap: var(--gf-space-3);
}

.staff__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--gf-space-3);
}

.staff__count {
  margin: 0;
}

.staff__notice {
  margin: 0;
  padding: var(--gf-space-3) var(--gf-space-4);
  border-radius: var(--gf-radius-md);
  background: var(--gf-color-surface-sunken);
}

.staff__row--editable {
  cursor: pointer;
}

.staff__row--editable:hover {
  background: var(--gf-color-surface-hover);
}

.staff__leftList {
  margin: var(--gf-space-2) 0 0;
  padding: 0;
  list-style: none;
}

.staff__leftPerson {
  padding: var(--gf-space-1) 0;
  border: 0;
  background: transparent;
  color: var(--gf-color-text);
  font: inherit;
  cursor: pointer;
}
</style>
